import { EllipsisIcon, FactoryIcon, TrashIcon } from "lucide-react";

import { CommentAuthorHeader } from "./CommentAuthorHeader";

import { Column, RichTextDisplay, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useDeleteRemoveExternalCandidacyComment } from "@/hooks/deleteRemoves/useDeleteRemoveExternalCandidacyComment";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import type { ExternalCandidacyComment as ExternalCandidacyCommentType } from "@/types";

// Staffer-authored rows resolve the author; external rows only have a name.
const StafferAuthorHeader = ({
  createdAt,
  userId,
}: {
  createdAt: string;
  userId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: author } = useGetUser({ userId });

  /* Render */
  const render = () => {
    return (
      <CommentAuthorHeader
        createdAt={createdAt}
        icon={
          <Row className="h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-700">
            <Text className="text-[10px] font-medium text-white">
              {author ? `${author.firstName[0]}${author.lastName[0]}` : "?"}
            </Text>
          </Row>
        }
        name={author ? `${author.firstName} ${author.lastName}` : "Staffer"}
      />
    );
  };

  return render();
};

export const ExternalComment = ({
  comment,
}: {
  comment: ExternalCandidacyCommentType;
}) => {
  /* Database CRUD Operations */
  // Delete
  const { mutate: deleteComment } = useDeleteRemoveExternalCandidacyComment();

  /* React Hooks */
  const currentUser = useGetCurrentUser();

  /* Helpers */
  // DELETE is owner-only on the backend, so only surface it to the author.
  const canDelete =
    !!currentUser &&
    comment.userId !== null &&
    currentUser.id === comment.userId;

  /* Handlers */
  const handleDelete = () => {
    mixpanelUtils.trackButtonClick(
      "Delete External Comment",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    deleteComment({
      candidacyId: comment.candidacyId,
      externalCandidacyCommentId: comment.id,
    });
  };

  /* Render */
  const renderAuthorHeader = () => {
    if (comment.userId !== null) {
      return (
        <StafferAuthorHeader
          createdAt={comment.createdAt}
          userId={comment.userId}
        />
      );
    }

    return (
      <CommentAuthorHeader
        createdAt={comment.createdAt}
        icon={
          <Row className="h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white">
            <FactoryIcon
              className="h-3.5 w-3.5 text-slate-700"
              strokeWidth={1.75}
            />
          </Row>
        }
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        name={comment.externalName || "External"}
      />
    );
  };

  const renderActionsMenu = () => {
    if (!canDelete) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost">
            <EllipsisIcon className="h-4 w-4 text-slate-700" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[110px] p-0">
          <DropdownMenuItem className="text-red-500" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const render = () => {
    return (
      <Column className="gap-1.5">
        <Row className="items-center justify-between gap-2">
          {renderAuthorHeader()}
          {renderActionsMenu()}
        </Row>
        <div className="whitespace-pre-line break-normal pl-8 text-sm font-normal leading-snug text-slate-600">
          <RichTextDisplay richText={comment.content} />
        </div>
      </Column>
    );
  };

  return render();
};
